/**
 * Advanced compare options sub-dialog.
 *
 * Houses the long-tail knobs that don't deserve real estate in the Setup
 * dialog body: case sensitivity, view/routine whitespace handling, and the
 * engine-filtered "properties to compare" checklist.
 */

import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { Settings2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SchemaCompareOptions } from '../../core/types/workspaceTypes';
import { applyEngineDefaults } from '../utils/compareDepth';
import { IgnoredPropertiesSection } from './IgnoredPropertiesSection';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display-only options (Standard-depth fallback already applied). */
  options: SchemaCompareOptions;
  /**
   * The setup dialog's `setOptions`. Updater form is required so a knob that
   * isn't a depth choice can't commit the fallback's derived
   * `detailed`/`ignoredProperties` into raw state (W5.2 fix round 1, F-2).
   */
  onChange: Dispatch<SetStateAction<SchemaCompareOptions>>;
  engine: DatabaseEngineId | undefined;
  disabled?: boolean;
  /**
   * Fix round 1 (Important #2) — set on the instance the results toolbar
   * opens directly (bypassing Setup). That instance edits `content.options`,
   * which only takes effect on the NEXT Compare — the diff on screen keeps
   * describing `diffResult.options` until then. Without this, the dialog was
   * indistinguishable from the setup-nested instance (whose own "Compare"
   * action re-runs immediately, so it never needs the caveat): every label in
   * here is present tense, and ticking a box produced a byte-identical screen
   * — same tree, same summary — which reads exactly like "my click did
   * nothing", the perception this whole wave exists to fix.
   */
  appliesOnNextRunOnly?: boolean;
};

export function SchemaCompareAdvancedDialog({
  open,
  onOpenChange,
  options,
  onChange,
  engine,
  disabled = false,
  appliesOnNextRunOnly = false,
}: Props) {
  // F-2 — ignoreCase / ignoreWhitespace are not depth choices, so they merge
  // into the RAW options rather than spreading the displayed object.
  const handleField = useCallback(
    (key: keyof SchemaCompareOptions, value: boolean) => {
      onChange((prev) => ({ ...prev, [key]: value }));
    },
    [onChange],
  );

  // Baseline defaults: ignoreCase=false (case differences stay visible),
  // ignoreWhitespace=true. User toggles persist; the effective value
  // below only fills in undefined slots.
  const effective = useMemo(() => applyEngineDefaults(options), [options]);
  // When Format SQL is on, the whitespace knob is redundant — disable it.
  const formatSqlOn = effective.formatSqlForComparison ?? true;

  // Ticking a property IS an explicit choice about what the diff ignores, so
  // this legitimately writes `ignoredProperties` into raw state — `next` is
  // computed by the checklist from the list the user actually saw. It still
  // uses the updater form so it never drags `detailed` along for the ride.
  const handleIgnoredPropertiesChange = useCallback(
    (next: string[]) => {
      onChange((prev) => ({ ...prev, ignoredProperties: next }));
    },
    [onChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="schema-compare-advanced-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Advanced compare options
          </DialogTitle>
          <DialogDescription>
            Fine-tune which property differences contribute to the diff.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 space-y-5 py-2">
          {appliesOnNextRunOnly && (
            <p
              className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md px-3 py-2"
              data-testid="schema-compare-advanced-next-run-notice"
            >
              Changes here apply to your next Compare — the results on screen
              won&rsquo;t change until you re-run it.
            </p>
          )}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="option-ignore-case"
                checked={effective.ignoreCase ?? false}
                onCheckedChange={(v) => handleField('ignoreCase', v === true)}
                disabled={disabled}
                data-testid="option-ignore-case"
              />
              <Label htmlFor="option-ignore-case" className="text-sm cursor-pointer">
                Ignore case differences in identifiers
              </Label>
            </div>
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="option-ignore-whitespace"
                  checked={effective.ignoreWhitespace ?? false}
                  onCheckedChange={(v) => handleField('ignoreWhitespace', v === true)}
                  // Format SQL during comparison already collapses whitespace
                  // (and keyword case) drift, so this finer knob is redundant
                  // when it's on. Greyed out, not removed — still useful in
                  // raw-mode comparisons.
                  disabled={disabled || formatSqlOn}
                  data-testid="option-ignore-whitespace"
                />
                <Label
                  htmlFor="option-ignore-whitespace"
                  className={`text-sm cursor-pointer ${formatSqlOn ? 'text-muted-foreground' : ''}`}
                >
                  Ignore whitespace in view/routine definitions
                </Label>
                {formatSqlOn && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                        data-testid="option-ignore-whitespace-redundant-badge"
                      >
                        Covered by Format SQL
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs leading-snug">
                      Already covered by "Format SQL during comparison" — the
                      formatter normalizes whitespace and keyword case on both
                      sides before comparison. Turn Format SQL off if you need
                      this finer knob.
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </TooltipProvider>
          </div>

          <IgnoredPropertiesSection
            ignoredProperties={options.ignoredProperties}
            onChange={handleIgnoredPropertiesChange}
            disabled={disabled}
            engine={engine}
          />
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} data-testid="schema-compare-advanced-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
